import NavbarV2 from '../../components/navbarV2/NavbarV2';
import './InvoicesV2.css';
import ListEmptyStateV2, { ListPageLoadingV2 } from '../../components/listEmptyStateV2/ListEmptyStateV2';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaChevronLeft, FaChevronRight, FaFileInvoice } from 'react-icons/fa';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { formatMoney, combinedAmount } from '../../utils/formatMoney';

const InvoicesV2 = () => {
    const [invoices, setInvoices] = useState([]);
    const [initialFetchDone, setInitialFetchDone] = useState(false);
    const [page, setPage] = useState(1);
    const [query, setQuery] = useState('');
    const [filter, setFilter] = useState('');
    const navigate = useNavigate();
    const prevSearch = useRef(null);
    const isAdmin = localStorage.getItem('admin') === 'true';

    const [debouncedQuery, flushQuery] = useDebouncedValue(query, 350);
    const [debouncedFilter, flushFilter] = useDebouncedValue(filter, 350);

    const loadInvoices = useCallback(
        (index, searchQ, searchF) => {
            let url = `${process.env.REACT_APP_API}/api/get${isAdmin ? 'AllInvoices' : 'Invoices/' + localStorage.getItem('account')}`;
            const params = new URLSearchParams();
            if (searchQ.length > 0) params.set('query', searchQ);
            if (searchF.length > 0) params.set('filter', searchF);
            params.set('page', String(index));
            url += '?' + params.toString();

            fetch(url, {})
                .then(async (response) => {
                    if (response.status === 200) {
                        if (localStorage.getItem('account') !== null) {
                            const data = await response.json();
                            setInvoices(data.filter((x) => x.status === 'APPROVED'));
                        } else {
                            navigate('/');
                        }
                    }
                })
                .catch(() => {})
                .finally(() => setInitialFetchDone(true));
        },
        [navigate, isAdmin],
    );

    useEffect(() => {
        if (!prevSearch.current) {
            prevSearch.current = { q: debouncedQuery, f: debouncedFilter };
            loadInvoices(page, debouncedQuery, debouncedFilter);
            return;
        }
        const searchChanged =
            prevSearch.current.q !== debouncedQuery || prevSearch.current.f !== debouncedFilter;

        if (searchChanged) {
            prevSearch.current = { q: debouncedQuery, f: debouncedFilter };
            if (page !== 1) {
                setPage(1);
            } else {
                loadInvoices(1, debouncedQuery, debouncedFilter);
            }
            return;
        }

        loadInvoices(page, debouncedQuery, debouncedFilter);
    }, [page, debouncedQuery, debouncedFilter, loadInvoices]);

    const runSearchNow = () => {
        flushQuery();
        flushFilter();
        setPage(1);
    };

    const clearFilters = () => {
        setQuery('');
        setFilter('');
        flushQuery();
        flushFilter();
        setPage(1);
    };

    const hasActiveFilters = Boolean(debouncedQuery || debouncedFilter);

    const sortedInvoices = useMemo(() => {
        const rowTime = (row) => {
            const d = row?.invoice?.date || row?.date || row?.createdAt;
            const t = d ? new Date(d).getTime() : 0;
            return Number.isFinite(t) ? t : 0;
        };
        return [...invoices].sort((a, b) => {
            const tb = rowTime(b);
            const ta = rowTime(a);
            if (tb !== ta) return tb - ta;
            return Number(b?.invoice?.invoiceNumber || 0) - Number(a?.invoice?.invoiceNumber || 0);
        });
    }, [invoices]);

    const getInvoicePayClass = (status) => {
        switch (String(status || '').toUpperCase()) {
            case 'UNPAID':
                return 'invoicesv2-inv--unpaid';
            case 'PAID':
                return 'invoicesv2-inv--paid';
            default:
                return 'invoicesv2-inv--default';
        }
    };

    return (
        <div className="invoicesv2">
            <NavbarV2 />

            <div className="invoicesv2-header">
                <h1 className="invoicesv2-title">Invoices</h1>
            </div>

            {isAdmin && (
                <div className="invoicesv2-filters">
                    <div className="invoicesv2-field">
                        <label>Search</label>
                        <input
                            type="text"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Invoice # (partial OK), name, email, phone…"
                        />
                    </div>
                    <div className="invoicesv2-field">
                        <label>Status</label>
                        <select value={filter} onChange={(e) => setFilter(e.target.value)}>
                            <option value="">ALL</option>
                            <option value="SUBMITTED">SUBMITTED</option>
                            <option value="APPROVED">APPROVED</option>
                            <option value="DECLINED">DECLINED</option>
                            <option value="SHIPPED">SHIPPED</option>
                        </select>
                    </div>
                    <div className="invoicesv2-filter-actions">
                        <button type="button" className="invoicesv2-btn invoicesv2-btn--primary" onClick={runSearchNow}>
                            Search now
                        </button>
                        <button
                            type="button"
                            className="invoicesv2-btn invoicesv2-btn--secondary"
                            onClick={() => navigate('/createcreditnote')}
                        >
                            Create Credit Note
                        </button>
                    </div>
                </div>
            )}

            {isAdmin && (
                <div className="invoicesv2-pagination">
                    <button type="button" className="invoicesv2-page-btn" onClick={() => page > 1 && setPage(page - 1)} disabled={page <= 1}>
                        <FaChevronLeft size={10} /> Previous
                    </button>
                    <p className="invoicesv2-page-num">{page}</p>
                    <button type="button" className="invoicesv2-page-btn" onClick={() => setPage(page + 1)}>
                        Next <FaChevronRight size={10} />
                    </button>
                </div>
            )}

            {!initialFetchDone && <ListPageLoadingV2 />}

            {initialFetchDone && invoices.length === 0 && !isAdmin && (
                <ListEmptyStateV2
                    icon={FaFileInvoice}
                    title="No invoices yet"
                    description="Once an order is approved, your invoice will appear here for easy reference and payment."
                    primaryAction={{ to: '/', label: 'Browse products' }}
                />
            )}

            {initialFetchDone && invoices.length === 0 && isAdmin && hasActiveFilters && (
                <ListEmptyStateV2
                    icon={FaFileInvoice}
                    title="No invoices found"
                    description="No approved invoices matched this search. Try different keywords or clear filters."
                    secondaryAction={{ label: 'Clear filters', onClick: clearFilters }}
                />
            )}

            {initialFetchDone && invoices.length === 0 && isAdmin && !hasActiveFilters && (
                <ListEmptyStateV2
                    icon={FaFileInvoice}
                    title="No approved invoices"
                    description="Approved customer invoices will show in this list. Nothing is available to display yet."
                />
            )}

            {sortedInvoices.length > 0 && (
                <div className="invoicesv2-table-wrapper">
                    <table className="invoicesv2-table">
                        <thead>
                            <tr>
                                <th>Invoice #</th>
                                <th>Date</th>
                                <th>Shipping</th>
                                <th>Payment</th>
                                <th>Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedInvoices.map((row) => (
                                <tr key={row._id} onClick={() => navigate(`/invoice/${row._id}`)}>
                                    <td className="invoicesv2-invoice-num">{row.invoice?.invoiceNumber}</td>
                                    <td>
                                        {new Intl.DateTimeFormat('en-GB', {
                                            year: 'numeric',
                                            month: '2-digit',
                                            day: '2-digit',
                                        }).format(new Date(row.date || row.createdAt))}
                                    </td>
                                    <td>
                                        <div className="invoicesv2-shipping-info">
                                            <p>{row.deliveryname || row.deliveryName}</p>
                                            <p>{row.deliverystreet || row.deliveryStreet}</p>
                                            <p>{row.deliverycity || row.deliveryCity}</p>
                                        </div>
                                    </td>
                                    <td>
                                        <span className={`invoicesv2-inv-status ${getInvoicePayClass(row.invoice?.status)}`}>
                                            {row.invoice?.status || '—'}
                                        </span>
                                    </td>
                                    <td className="invoicesv2-total">
                                        €{formatMoney(combinedAmount(row))}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default InvoicesV2;
